import React, { useCallback, useContext, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { Alert, Button, Form, Pagination } from "react-bootstrap";
import { MdAdd, MdDelete, MdEdit } from "react-icons/md";
import { AuthContext } from "../../../contexts/AuthContext";
import { tempUrl } from "../../../contexts/ContextProvider";
import {
  ConfirmDeleteDialog,
  InfoDialog,
  Loader,
  ScrollToTop,
  SearchBar,
} from "../../../components";
import { ShowTableBarang } from "../../../components/ShowTable";
import { NumericFormat } from "react-number-format";

const TampilBarang = () => {
  const { user, dispatch } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();
  const selectedId = location.pathname.split("/")[2];

  const [barangs, setBarangs] = useState([]);
  const [selectedBarang, setSelectedBarang] = useState(null);
  const [query, setQuery] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(0);
  const [totalPage, setTotalPage] = useState(0);
  const [totalRows, setTotalRows] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteError, setDeleteError] = useState("");

  const handleAuthError = useCallback(
    (err) => {
      if (err.response?.status === 401) {
        dispatch({ type: "LOGOUT" });
        navigate("/");
      }
    },
    [dispatch, navigate],
  );

  const getBarangs = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.post(
        `${tempUrl}/barangsPagination?search_query=${encodeURIComponent(
          searchTerm,
        )}&page=${page}&limit=20`,
        {
          _id: user.id,
          token: user.token,
        },
      );
      setBarangs(response.data.barangs);
      setTotalPage(response.data.totalPage);
      setTotalRows(response.data.totalRows);
    } catch (err) {
      handleAuthError(err);
      setError(err.response?.data?.message || "Gagal mengambil data Barang.");
    }
    setLoading(false);
  }, [handleAuthError, page, searchTerm, user.id, user.token]);

  const getBarangById = useCallback(async () => {
    if (!selectedId) {
      setSelectedBarang(null);
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${tempUrl}/barangs/${selectedId}`, {
        _id: user.id,
        token: user.token,
      });
      setSelectedBarang(response.data);
    } catch (err) {
      handleAuthError(err);
      setError(err.response?.data?.message || "Gagal mengambil detail Barang.");
    }
    setLoading(false);
  }, [handleAuthError, selectedId, user.id, user.token]);

  useEffect(() => {
    getBarangs();
  }, [getBarangs]);

  useEffect(() => {
    getBarangById();
  }, [getBarangById]);

  const searchData = (event) => {
    event.preventDefault();
    setPage(0);
    setSearchTerm(query);
  };

  const deleteBarang = async () => {
    setShowDeleteConfirm(false);
    setLoading(true);
    try {
      await axios.post(`${tempUrl}/deleteBarang/${selectedId}`, {
        _id: user.id,
        token: user.token,
      });
      navigate("/barang");
      setSelectedBarang(null);
      await getBarangs();
    } catch (err) {
      handleAuthError(err);
      setDeleteError(err.response?.data?.message || "Gagal menghapus Barang.");
      setLoading(false);
    }
  };

  if (loading) {
    return <Loader />;
  }

  return (
    <div>
      <ScrollToTop />
      <div className="page-header">
        <div>
          <div className="page-kicker">Master</div>
          <h1 className="page-title">Barang</h1>
        </div>
        <Button
          className="btn-icon"
          size="sm"
          onClick={() => navigate("/barang/tambahBarang")}
        >
          <MdAdd /> Tambah
        </Button>
      </div>
      {error && <Alert variant="warning">{error}</Alert>}

      {selectedBarang && (
        <section className="section-panel">
          <div className="toolbar mt-0">
            <div className="detail-title-actions">
              <Button
                className="btn-back"
                size="sm"
                variant="outline-secondary"
                onClick={() => navigate("/barang")}
              >
                {"< Kembali"}
              </Button>
              <div>
                <h2 className="h6 fw-bold mb-1">Detail Barang</h2>
                <div className="text-muted">{selectedBarang.nama}</div>
              </div>
            </div>
            <div className="d-flex gap-2 flex-wrap">
              <Button
                className="btn-icon"
                size="sm"
                variant="outline-primary"
                onClick={() => navigate(`/barang/${selectedBarang.id}/edit`)}
              >
                <MdEdit /> Ubah
              </Button>
              <Button
                className="btn-icon"
                size="sm"
                variant="outline-danger"
                onClick={() => setShowDeleteConfirm(true)}
              >
                <MdDelete /> Hapus
              </Button>
            </div>
          </div>
          <hr />
          <div className="mb-3">
            <div className="detail-label">Kategori</div>
            <div className="detail-value">
              {selectedBarang.kategori?.nama || "-"}
            </div>
          </div>
          <div className="mb-3">
            <div className="detail-label">Merk</div>
            <div className="detail-value">{selectedBarang.merk?.nama || "-"}</div>
          </div>
          <div className="mb-3">
            <div className="detail-label">Kode</div>
            <div className="detail-value">{selectedBarang.kode || "-"}</div>
          </div>
          <div className="mb-3">
            <div className="detail-label">Nama</div>
            <div className="detail-value">{selectedBarang.nama || "-"}</div>
          </div>
          <div className="mb-3">
            <div className="detail-label">Deskripsi</div>
            <div className="detail-value">{selectedBarang.deskripsi || "-"}</div>
          </div>
          <div className="mb-3">
            <div className="detail-label">Harga Jual</div>
            <div className="detail-value">
              <NumericFormat
                value={selectedBarang.hargaJual || "0"}
                valueIsNumericString
                displayType="text"
                thousandSeparator="."
                decimalSeparator=","
                decimalScale={2}
                allowNegative={false}
              />
            </div>
          </div>
          <div className="mb-3">
            <div className="detail-label">Flash Sale</div>
            <div className="detail-value">
              {selectedBarang.isFlashsale ? "Ya" : "Tidak"}
            </div>
          </div>
          <div className="mb-3">
            <div className="detail-label">Harga Promo</div>
            <div className="detail-value">
              <NumericFormat
                value={selectedBarang.hargaPromo || "0"}
                valueIsNumericString
                displayType="text"
                thousandSeparator="."
                decimalSeparator=","
                decimalScale={2}
                allowNegative={false}
              />
            </div>
          </div>
          <div className="mb-3">
            <div className="detail-label">Stok</div>
            <div className="detail-value">{selectedBarang.stok || 0}</div>
          </div>
          <div className="mb-3">
            <div className="detail-label">Minimal Qty Grosir</div>
            <div className="detail-value">{selectedBarang.minimalQtyGrosir || 0}</div>
          </div>
          <div className="mb-3">
            <div className="detail-label">Harga Grosir</div>
            <div className="detail-value">
              <NumericFormat
                value={selectedBarang.hargaGrosir || "0"}
                valueIsNumericString
                displayType="text"
                thousandSeparator="."
                decimalSeparator=","
                decimalScale={2}
                allowNegative={false}
              />
            </div>
          </div>
          <div className="mb-3">
            <div className="detail-label">Foto Barang</div>
            {selectedBarang.barangfotos?.length > 0 ? (
              <div className="d-flex gap-2 flex-wrap">
                {selectedBarang.barangfotos.map((foto, index) => (
                  <a
                    key={foto.id || index}
                    href={foto.linkGambar}
                    target="_blank"
                    rel="noreferrer"
                  >
                    <img
                      src={foto.linkGambar}
                      alt={`Barang ${index + 1}`}
                      className="rounded border"
                      style={{
                        width: "120px",
                        height: "90px",
                        objectFit: "cover",
                      }}
                    />
                  </a>
                ))}
              </div>
            ) : (
              <div className="detail-value">-</div>
            )}
          </div>
        </section>
      )}

      <Form onSubmit={searchData}>
        <div className="toolbar">
          <div className="d-flex gap-2 flex-wrap">
            <SearchBar setSearchTerm={setQuery} value={query} />
            <Button className="btn-icon" type="submit">
              Cari
            </Button>
          </div>
          <div className="text-muted small">Total data: {totalRows}</div>
        </div>
      </Form>

      <ShowTableBarang
        barangs={barangs}
        selectedId={selectedId}
        onSelect={(id) => navigate(`/barang/${id}`)}
      />

      {totalPage > 1 && (
        <Pagination className="mt-3">
          <Pagination.Prev
            disabled={page === 0}
            onClick={() => setPage(page - 1)}
          />
          {Array.from({ length: totalPage }, (_, index) => (
            <Pagination.Item
              key={index}
              active={index === page}
              onClick={() => setPage(index)}
            >
              {index + 1}
            </Pagination.Item>
          ))}
          <Pagination.Next
            disabled={page + 1 >= totalPage}
            onClick={() => setPage(page + 1)}
          />
        </Pagination>
      )}

      <ConfirmDeleteDialog
        show={showDeleteConfirm}
        message={`Yakin ingin menghapus Barang ${selectedBarang?.nama}?`}
        onCancel={() => setShowDeleteConfirm(false)}
        onConfirm={deleteBarang}
      />
      <InfoDialog
        show={Boolean(deleteError)}
        title="Data Tidak Bisa Dihapus"
        message={deleteError}
        onClose={() => setDeleteError("")}
      />
    </div>
  );
};

export default TampilBarang;
